/**
 * AI Assistant Dashboard
 * Tabs: Dashboard (status + chat), Settings, Manage, Number
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { authFetch, refreshToken } from '../../auth';
import useSessionPresenceWebSocket from '../../utils/useSessionPresenceWebSocket';
import ChatMessage from './ChatMessage';
// WhatsAppTab removed — bridge deprecated
// Number tab removed — replaced by Numbers subtab in Settings
import { SettingsTab } from './AIComponents';
import ShareSheet from '../ShareSheet';
import './AIAgentDashboard.css';

const TABS = ['Chat', 'Settings'];
const SESSION_COLORS = ['#1E203F', '#e65100', '#6a1b9a', '#1565c0', '#FF9B13', '#c62828', '#4527a0'];

const sortByTimestamp = (items = [], field = 'timestamp') => (
  [...items].sort((a, b) => new Date(a[field] || 0) - new Date(b[field] || 0))
);
const sortByTimestampDesc = (items = [], field = 'timestamp') => (
  [...items].sort((a, b) => new Date(b[field] || 0) - new Date(a[field] || 0))
);

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const FALLBACK_MODEL_CATALOG = [
  { id: 'google/gemini-2.5-flash', display_name: 'Gemini 2.5 Flash', family: 'gemini', tier: 'fast', status: 'active', aliases: ['google/gemini-2.5-flash-preview', 'gemini-2.5-flash'] },
  { id: 'google/gemini-2.5-pro', display_name: 'Gemini 2.5 Pro', family: 'gemini', tier: 'strong', status: 'active', aliases: ['google/gemini-2.5-pro-preview', 'gemini-2.5-pro'] },
  { id: 'google/gemini-3-flash-preview', display_name: 'Gemini 3 Flash Preview', family: 'gemini', tier: 'fast', status: 'preview', aliases: ['gemini-3-flash-preview'] },
  { id: 'google/gemini-3.1-flash-lite-preview', display_name: 'Gemini 3.1 Flash Lite Preview', family: 'gemini', tier: 'cheap', status: 'preview', aliases: ['gemini-3.1-flash-lite-preview'] },
  { id: 'anthropic/claude-haiku-4.5', display_name: 'Claude Haiku 4.5', family: 'claude', tier: 'fast', status: 'active', aliases: ['anthropic/claude-haiku-4', 'anthropic/claude-3.5-haiku'] },
  { id: 'anthropic/claude-sonnet-4', display_name: 'Claude Sonnet 4', family: 'claude', tier: 'strong', status: 'active', aliases: ['anthropic/claude-3.5-sonnet'] },
  { id: 'cloudflare/llama-3.3-70b', display_name: 'Llama 3.3 70B', family: 'cloudflare', tier: 'strong', status: 'active', aliases: ['@cf/meta/llama-3.3-70b-instruct-fp8-fast'] },
  { id: 'cloudflare/qwen-2.5-coder-32b', display_name: 'Qwen 2.5 Coder 32B', family: 'cloudflare', tier: 'fast', status: 'active', aliases: ['@cf/qwen/qwen2.5-coder-32b-instruct'] },
  { id: 'cloudflare/deepseek-r1-distill-qwen-32b', display_name: 'DeepSeek R1 Qwen 32B', family: 'cloudflare', tier: 'strong', status: 'active', aliases: ['@cf/deepseek-ai/deepseek-r1-distill-qwen-32b'] },
  { id: 'openai/gpt-4o-mini', display_name: 'GPT-4o Mini', family: 'gpt', tier: 'fast', status: 'active', aliases: ['gpt-4o-mini'] },
  { id: 'openai/gpt-4o', display_name: 'GPT-4o', family: 'gpt', tier: 'strong', status: 'active', aliases: ['gpt-4o'] },
  { id: 'meta-llama/llama-4-scout', display_name: 'Llama 4 Scout', family: 'llama', tier: 'open', status: 'active', aliases: ['llama-4-scout'] },
  { id: 'minimax/MiniMax-M2.7', display_name: 'MiniMax M2.7', family: 'minimax', tier: 'strong', status: 'active', aliases: ['MiniMax-M2.7'] },
  { id: 'minimax/MiniMax-M2.5', display_name: 'MiniMax M2.5', family: 'minimax', tier: 'fast', status: 'active', aliases: ['MiniMax-M2.5'] },
  { id: 'perplexity/sonar', display_name: 'Perplexity Sonar', family: 'perplexity', tier: 'fast', status: 'active', aliases: ['sonar'], noTools: true },
  { id: 'perplexity/sonar-pro', display_name: 'Perplexity Sonar Pro', family: 'perplexity', tier: 'strong', status: 'active', aliases: ['sonar-pro'], noTools: true },
  { id: 'perplexity/sonar-reasoning', display_name: 'Perplexity Sonar Reasoning', family: 'perplexity', tier: 'strong', status: 'active', aliases: ['sonar-reasoning'], noTools: true },
  { id: 'perplexity/sonar-reasoning-pro', display_name: 'Perplexity Sonar Reasoning Pro', family: 'perplexity', tier: 'strong', status: 'active', aliases: ['sonar-reasoning-pro'], noTools: true }
];

const MODEL_FAMILY_LABELS = {
  gemini: 'Google',
  claude: 'Anthropic',
  cloudflare: 'Cloudflare',
  gpt: 'OpenAI',
  llama: 'Meta',
  minimax: 'MiniMax',
  perplexity: 'Perplexity'
};

const MODEL_TIER_LABELS = {
  cheap: 'cheapest',
  fast: 'fast',
  strong: 'strong',
  open: 'open'
};

const normalizeCatalogModel = (model, catalog) => {
  const raw = String(model || '').trim();
  if (!raw) return '';
  const entries = Array.isArray(catalog) && catalog.length ? catalog : FALLBACK_MODEL_CATALOG;
  const match = entries.find((entry) => {
    if (entry.id === raw) return true;
    if ((entry.aliases || []).includes(raw)) return true;
    return Object.values(entry.provider_variants || {}).includes(raw);
  });
  return match ? match.id : raw;
};

const historyContainsTurn = (sessionList, userText, assistantText) => (
  (sessionList || []).some(session => {
    const msgs = session.messages || [];
    return msgs.some(msg => msg.role === 'user' && msg.content === userText) &&
      msgs.some(msg => msg.role === 'assistant' && msg.content === assistantText);
  })
);

const normalizeMessageToolErrors = (msg) => {
  const failedTools = (msg?.toolCalls || []).filter(tc => tc && tc.error);
  return {
    ...msg,
    toolCalls: failedTools.length > 0 ? failedTools : undefined,
    llmTrace: msg?.llmTrace || undefined,
    reasoning: msg?.reasoning || msg?.reasoning_content || undefined
  };
};

const formatLlmTrace = (trace) => {
  if (!trace) return '';
  const parts = [];
  if (trace.route) parts.push(trace.route);
  const model = trace.effectiveModel || trace.requestedModel;
  if (model) parts.push(String(model).replace(/^[^/]+\//, ''));
  if (trace.fallbackStage) parts.push(trace.fallbackStage);
  return parts.join(' · ');
};

const formatMsgTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isToday) return time;
  if (isYesterday) return `Yesterday ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + time;
};


const AIAgentDashboard = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const debugSeenRef = useRef(new Set());
  const handleAgentWsMessage = useCallback((event) => {
    try {
      const msg = JSON.parse(event.data);
      if (msg.type === 'voice_debug' && msg.data) {
        const key = (msg.data.ts || '') + '|' + (msg.data.step || '');
        if (debugSeenRef.current.has(key)) return;
        debugSeenRef.current.add(key);
        if (debugSeenRef.current.size > 200) {
          const arr = Array.from(debugSeenRef.current);
          debugSeenRef.current = new Set(arr.slice(-100));
        }
        setVoiceDebug(prev => [...prev.slice(-100), msg.data]);
        // Track tool activity for activity bar during sending
        if (sendingRef.current && (msg.data.step === 'tool_call' || msg.data.step === 'tool_result')) {
          setActiveTools(prev => [...prev.slice(-5), { step: msg.data.step, name: msg.data.name, ts: msg.data.ts }]);
        }
      }
      if (msg.type === 'agent_response' && msg.jobId) {
        setSending(false);
        sendingRef.current = false;
        setActiveTools([]);
        setMessages(prev => {
          const target = prev.find(m => m.jobId === msg.jobId);
          if (target && target.userMessage && msg.response) {
            try { syncHistoryAfterSendRef.current && syncHistoryAfterSendRef.current(target.userMessage, msg.response); } catch (_) {}
          }
          if (voiceModeRef.current && msg.response) {
            try { speakTextRef.current && speakTextRef.current(msg.response); } catch (_) {}
          }
          const failedTools = ((msg.toolCalls || []).filter(tc => tc && tc.error));
          return prev.map(m => m.jobId === msg.jobId
            ? { ...m, content: msg.response, toolCalls: failedTools.length > 0 ? failedTools : undefined, llmTrace: msg.llmTrace || undefined, reasoning: msg.reasoning || undefined, pending: false, timestamp: msg.timestamp || m.timestamp, userMessage: undefined }
            : m);
        });
      }
      if (msg.type === 'agent_error' && msg.jobId) {
        setSending(false);
        sendingRef.current = false;
        setActiveTools([]);
        setMessages(prev => prev.map(m => m.jobId === msg.jobId
          ? { ...m, content: msg.error || 'Agent error', error: true, pending: false }
          : m));
      }
    } catch(e) {}
  }, []);
  const syncHistoryAfterSendRef = useRef(null);
  const speakTextRef = useRef(null);
  useSessionPresenceWebSocket(!!user, 'AI Agent', handleAgentWsMessage);
  const [activeTab, setActiveTab] = useState(location.state?.applyTemplate ? 'Settings' : 'Chat');
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [modelCatalog, setModelCatalog] = useState(FALLBACK_MODEL_CATALOG);
  const [allowedModels, setAllowedModels] = useState(null);
  const [savingModel, setSavingModel] = useState(false);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const modelPickerRef = useRef(null);
  const modelTriggerRef = useRef(null);
  const modelPopoverRef = useRef(null);
  const [modelPopoverCoords, setModelPopoverCoords] = useState(null);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [listening, setListening] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [selectedMsgs, setSelectedMsgs] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [shareMsg, setShareMsg] = useState(null);
  const [showAllThinking, setShowAllThinking] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('__new__');
  // Client-generated sessionId for the in-flight __new__ chat. Generated lazily on first send
  // (so we don't pollute history with empty sessions) and reset by newChat().
  const [pendingNewSessionId, setPendingNewSessionId] = useState(null);
  const [sendTarget, setSendTarget] = useState('ai');
  const [sendPickerOpen, setSendPickerOpen] = useState(false);
  const sendTriggerRef = useRef(null);
  const sendPopoverRef = useRef(null);
  const [sessionPickerOpen, setSessionPickerOpen] = useState(false);
  const sessionPickerTriggerRef = useRef(null);
  const [selectedTier, setSelectedTier] = useState('all');
  const [chatAs, setChatAs] = useState('owner');
  const [liveTranscript, setLiveTranscript] = useState('');
  const [historySyncing, setHistorySyncing] = useState(false);
  const [voiceDebug, setVoiceDebug] = useState([]);
  const [activeTools, setActiveTools] = useState([]);
  const [activityTick, setActivityTick] = useState(0);
  const [showDebug, setShowDebug] = useState(false);
  const recognitionRef = useRef(null);
  const voiceModeRef = useRef(false);
  const chatMessagesRef = useRef(null);
  const chatBottomRef = useRef(null);
  const cancelAutoPinRef = useRef(false);
  const programmaticScrollRef = useRef(false);
  const userScrollIntentRef = useRef(false);
  const programmaticScrollRafRef = useRef(0);
  const autoFollowLockUntilRef = useRef(0);
  const lastReplyRef = useRef('');

  const isNearBottom = useCallback((el) => {
    if (!el) return true;
    return (el.scrollHeight - el.clientHeight - el.scrollTop) <= 60;
  }, []);

  const scrollChatToBottom = useCallback(() => {
    const chatMessagesEl = chatMessagesRef.current;
    if (!chatMessagesEl) return;
    if (programmaticScrollRafRef.current) window.cancelAnimationFrame(programmaticScrollRafRef.current);
    programmaticScrollRef.current = true;
    const maxScrollTop = Math.max(chatMessagesEl.scrollHeight - chatMessagesEl.clientHeight, 0);
    chatMessagesEl.scrollTop = maxScrollTop;
    chatBottomRef.current?.scrollIntoView({ block: 'end' });
    programmaticScrollRafRef.current = window.requestAnimationFrame(() => {
      chatMessagesEl.scrollTop = Math.max(chatMessagesEl.scrollHeight - chatMessagesEl.clientHeight, 0);
      programmaticScrollRef.current = false;
      programmaticScrollRafRef.current = 0;
    });
  }, []);

  const lockAutoFollow = useCallback((durationMs = 5000) => {
    autoFollowLockUntilRef.current = Date.now() + durationMs;
    cancelAutoPinRef.current = false;
    userScrollIntentRef.current = false;
  }, []);

  const jumpToLatest = useCallback(() => {
    lockAutoFollow(2000);
    scrollChatToBottom();
  }, [lockAutoFollow, scrollChatToBottom]);

  const newChat = () => {
    setMessages([]);
    setSelectedSession('__new__');
    setChatInput('');
    setPendingNewSessionId(null); // generated lazily on first send
  };

  // Client-side sessionId used for the in-flight __new__ chat. Generated on first send so
  // we don't pollute history with sessions that were never used. Kept stable across messages
  // in the same __new__ chat so they all land in the same server-side session.
  function ensurePendingNewSessionId() {
    if (pendingNewSessionId) return pendingNewSessionId;
    var rnd = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
      : Math.random().toString(36).slice(2, 14);
    var id = 'web-' + new Date().toISOString().replace(/[:.]/g, '-') + '-' + rnd;
    setPendingNewSessionId(id);
    return id;
  }

  // Load status on mount
  useEffect(() => {
    loadStatus();
    loadConfig();
    loadHistory();
    authFetch('/api/aiassistant/models')
      .then(r => r.ok ? r.json() : null)
      .then((data) => {
        const models = Array.isArray(data?.models) && data.models.length ? data.models : FALLBACK_MODEL_CATALOG;
        setModelCatalog(models);
        if (data?.allowed_models !== undefined) setAllowedModels(data.allowed_models);
      })
      .catch(() => {});
  }, []);

  // `messages` is the in-flight buffer ONLY: optimistic user msg, pending assistant reply.
  // Committed content lives in `sessions` and renders via `historicalSessions` below.
  // Previously this effect ALSO copied session messages into `messages`, which caused every
  // bubble to render twice — once via historicalSessions, once via the live block — visible
  // as "Is it public?" appearing twice in the AI chat thread.
  // On an explicit session switch we clear the in-flight buffer so it can't bleed into the
  // newly-selected session. Sessions-array refreshes don't touch it (history catches up via
  // historicalSessions naturally).
  var prevSelectedSessionRef = useRef(selectedSession);
  useEffect(function() {
    if (activeTab !== 'Chat') return;
    var sessionChanged = prevSelectedSessionRef.current !== selectedSession;
    prevSelectedSessionRef.current = selectedSession;
    if (sessionChanged) setMessages([]);
  }, [selectedSession, sessions]);

  // Ticks every second during sending to update elapsed time in activity bar
  useEffect(function() {
    if (!sending) return;
    var timer = setInterval(function() { setActivityTick(t => t + 1); }, 1000);
    return function() { clearInterval(timer); };
  }, [sending]);

  useEffect(() => {
    if (activeTab === 'Chat') loadHistory();
  }, [activeTab]);

  useLayoutEffect(() => {
    if (loading || activeTab !== 'Chat') return;
    lockAutoFollow(5000);
    if (!cancelAutoPinRef.current) scrollChatToBottom();
  }, [loading, activeTab, liveTranscript, messages, selectedSession, sending, sessions, lockAutoFollow, scrollChatToBottom]);

  useEffect(() => {
    if (loading || activeTab !== 'Chat') return;
    const chatMessagesEl = chatMessagesRef.current;
    if (!chatMessagesEl) return;
    lockAutoFollow(5000);
    let rafId = 0;

    const markUserScrollIntent = () => {
      userScrollIntentRef.current = true;
      if (!isNearBottom(chatMessagesEl)) cancelAutoPinRef.current = true;
    };

    const handleScroll = () => {
      if (programmaticScrollRef.current) return;
      if (isNearBottom(chatMessagesEl)) {
        cancelAutoPinRef.current = false;
        userScrollIntentRef.current = false;
        return;
      }
      if (userScrollIntentRef.current) {
        cancelAutoPinRef.current = true;
        return;
      }
      if (Date.now() < autoFollowLockUntilRef.current) {
        scrollChatToBottom();
      }
    };

    const pinIfFollowing = () => {
      if (!cancelAutoPinRef.current) scrollChatToBottom();
    };

    const maintainBottomDuringLock = () => {
      if (!cancelAutoPinRef.current && Date.now() < autoFollowLockUntilRef.current) {
        scrollChatToBottom();
        rafId = window.requestAnimationFrame(maintainBottomDuringLock);
      }
    };

    const mutationObserver = new MutationObserver(() => {
      pinIfFollowing();
    });
    mutationObserver.observe(chatMessagesEl, { childList: true, subtree: true, characterData: true });
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
          pinIfFollowing();
        })
      : null;
    resizeObserver?.observe(chatMessagesEl);
    chatMessagesEl.addEventListener('scroll', handleScroll, { passive: true });
    chatMessagesEl.addEventListener('wheel', markUserScrollIntent, { passive: true });
    chatMessagesEl.addEventListener('touchstart', markUserScrollIntent, { passive: true });
    chatMessagesEl.addEventListener('pointerdown', markUserScrollIntent, { passive: true });
    rafId = window.requestAnimationFrame(maintainBottomDuringLock);
    return () => {
      window.cancelAnimationFrame(rafId);
      if (programmaticScrollRafRef.current) window.cancelAnimationFrame(programmaticScrollRafRef.current);
      programmaticScrollRafRef.current = 0;
      programmaticScrollRef.current = false;
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      chatMessagesEl.removeEventListener('scroll', handleScroll);
      chatMessagesEl.removeEventListener('wheel', markUserScrollIntent);
      chatMessagesEl.removeEventListener('touchstart', markUserScrollIntent);
      chatMessagesEl.removeEventListener('pointerdown', markUserScrollIntent);
    };
  }, [loading, activeTab, selectedSession, isNearBottom, lockAutoFollow, scrollChatToBottom]);

  const loadStatus = async () => {
    try {
      const res = await authFetch('/api/aiassistant/status');
      const data = await res.json();
      setStatus(data);
    } catch (e) {
      console.error('Failed to load agent status:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const res = await authFetch('/api/aiassistant/config');
      if (res.ok) setConfig(await res.json());
    } catch (e) {}
  };

  const loadHistory = async () => {
    try {
      const res = await authFetch('/api/aiassistant/history?limit=200');
      if (res.ok) {
        const data = await res.json();
        const normalizedSessions = (data.sessions || []).map(session => ({
          ...session,
          messages: (session.messages || []).map(normalizeMessageToolErrors)
        }));
        setSessions(normalizedSessions);
        // Seed lastSeen on first load so only truly new messages show NEW badge
        try {
          normalizedSessions.forEach(function(s) {
            var key = 'nf_last_seen_' + s.sessionId;
            if (!localStorage.getItem(key)) {
              var msgs = s.messages || [];
              localStorage.setItem(key, msgs.length ? new Date(msgs[msgs.length - 1].timestamp).getTime() : Date.now());
            }
          });
        } catch(e) {}
        // Force scroll to bottom on initial load
        setTimeout(() => {
          if (chatMessagesRef.current) {
            cancelAutoPinRef.current = false;
            scrollChatToBottom();
          }
        }, 100);
        return normalizedSessions;
      }
    } catch (e) {}
    return null;
  };

  const syncHistoryAfterSend = async (userText, assistantText) => {
    setHistorySyncing(true);
    try {
      for (const waitMs of [0, 250, 750]) {
        if (waitMs) await delay(waitMs);
        const updatedSessions = await loadHistory();
        if (updatedSessions && historyContainsTurn(updatedSessions, userText, assistantText)) {
          setMessages([]);
          if (selectedSession === '__new__') {
            // Prefer the client-minted pendingNewSessionId — it's authoritative because we sent it.
            // Fall back to message scan in case server assigned a different id.
            var nextId = pendingNewSessionId;
            if (!nextId) {
              var newSession = (updatedSessions || []).find(function(s) {
                return (s.messages || []).some(function(m) { return m.role === 'user' && m.content === userText; });
              });
              if (newSession && newSession.sessionId) nextId = newSession.sessionId;
            }
            if (nextId) {
              setSelectedSession(nextId);
              setPendingNewSessionId(null);
            }
          }
          setHistorySyncing(false);
          return true;
        }
      }
    } finally {
      setHistorySyncing(false);
    }
    return false;
  };

  const enableAgent = async () => {
    setEnabling(true);
    try {
      const res = await authFetch('/api/aiassistant/enable', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      const data = await res.json();
      if (data.success) {
        // Poll status until running
        const poll = setInterval(async () => {
          const sr = await authFetch('/api/aiassistant/status');
          const sd = await sr.json();
          setStatus(sd);
          if (sd.status === 'running') {
            clearInterval(poll);
            setEnabling(false);
          }
        }, 3000);
        // Stop polling after 60s
        setTimeout(() => { clearInterval(poll); setEnabling(false); }, 60000);
      } else {
        alert(data.error || 'Failed to enable');
        setEnabling(false);
      }
    } catch (e) {
      alert('Failed to enable agent');
      setEnabling(false);
    }
  };

  const disableAgent = async () => {
    if (!window.confirm('Stop your AI agent? You can re-enable it anytime.')) return;
    const res = await authFetch('/api/aiassistant/disable', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
    if (res.ok) {
      setStatus({ enabled: false, status: 'not_configured' });
    }
  };

  const currentModel = normalizeCatalogModel(config?.config?.llmModel || 'anthropic/claude-haiku-4.5', modelCatalog);
  const shortModelName = (id, entry) => {
    const e = entry || (modelCatalog || []).find(m => m.id === id);
    if (e?.display_name) return e.display_name.replace(/^(Claude|Google|Gemini|Anthropic|OpenAI|GPT-?|Meta|Llama|Cloudflare|MiniMax)\s+/i, '');
    return String(id || '').replace(/^[^/]+\//, '');
  };
  const tierDescription = (tier) => {
    if (tier === 'strong') return 'Most capable for ambitious work';
    if (tier === 'fast') return 'Most efficient for everyday tasks';
    if (tier === 'cheap') return 'Fastest for quick answers';
    if (tier === 'open') return 'Open source';
    return '';
  };
  const tierPriceLevel = (tier) => {
    if (tier === 'strong') return 4;
    if (tier === 'fast') return 3;
    if (tier === 'cheap') return 1;
    if (tier === 'open') return 1;
    return 2;
  };
  const tierStars = (tier) => {
    if (tier === 'strong') return 4;
    if (tier === 'fast') return 3;
    if (tier === 'cheap') return 1;
    if (tier === 'open') return 2;
    return 2;
  };
  const openModelPicker = () => {
    if (modelTriggerRef.current) {
      const rect = modelTriggerRef.current.getBoundingClientRect();
      const POP_W = Math.min(320, window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - POP_W - 8));
      setModelPopoverCoords({
        bottom: window.innerHeight - rect.top + 8,
        left,
        width: POP_W,
        maxHeight: Math.min(window.innerHeight - 80, 480),
      });
    }
    setModelPickerOpen(o => !o);
  };
  useEffect(() => {
    if (!modelPickerOpen) return;
    const onDocClick = (e) => {
      const inTrigger = modelTriggerRef.current && modelTriggerRef.current.contains(e.target);
      const inPop = modelPopoverRef.current && modelPopoverRef.current.contains(e.target);
      if (!inTrigger && !inPop) setModelPickerOpen(false);
    };
    const onResize = () => setModelPickerOpen(false);
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [modelPickerOpen]);

  useEffect(() => {
    if (!sessionPickerOpen) return;
    var onDocClick = function(e) {
      if (sessionPickerTriggerRef.current && sessionPickerTriggerRef.current.contains(e.target)) return;
      var pop = document.querySelector('.chat-session-popover');
      if (pop && pop.contains(e.target)) return;
      setSessionPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return function() { document.removeEventListener('mousedown', onDocClick); };
  }, [sessionPickerOpen]);
  const availableModels = useMemo(() => {
    var base = (modelCatalog || []);
    if (allowedModels === null) base = modelCatalog || [];
    else if (!Array.isArray(allowedModels) || !allowedModels.length) base = modelCatalog || [];
    else base = (modelCatalog || []).filter(m => allowedModels.includes(m.id) || (m.aliases || []).some(a => allowedModels.includes(a)));
    return base.filter(function(m) { return !m.noTools; });
  }, [modelCatalog, allowedModels]);

  const changeModel = async (newModel) => {
    setSavingModel(true);
    try {
      const res = await authFetch('/api/aiassistant/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ llmModel: newModel })
      });
      if (res.ok) await loadConfig();
    } catch (_) {}
    finally { setSavingModel(false); }
  };

  const sendChat = async (e, forceTarget) => {
    e && e.preventDefault();
    var target = forceTarget || sendTarget;
    if (!chatInput.trim() || sending || sendingRef.current) return;
    sendingRef.current = true;

    var msg = chatInput.trim();
    setChatInput('');

    // @ai prefix: strip and route to AI (even in SMS sessions)
    var forceAi = msg.startsWith('@ai');
    if (forceAi) msg = msg.substring(3).trim();

    // SMS or voice session with a known caller number + no @ai: send directly to guest by SMS
    if (target === 'sms' && canTextCaller && !forceAi) {
      setMessages(prev => [...prev, { mid: 'u_' + Date.now(), role: 'user', content: msg, timestamp: new Date() }]);
      try {
        await authFetch('/api/conversations/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: smsCaller, body: msg, from: (config?.phoneNumber || '') })
        });
        setMessages(prev => [...prev, { mid: 'sms_' + Date.now(), role: 'system', content: 'SMS sent to ' + smsCaller, timestamp: new Date() }]);
      } catch (e) {
        setMessages(prev => [...prev, { role: 'assistant', content: 'SMS send failed: ' + (e.message || 'error'), timestamp: new Date(), error: true }]);
      }
      return;
    }

    // Optimistic user msg + IMMEDIATE pending assistant placeholder so the user always
    // sees an in-flight bubble the instant they tap Send (rendered as the "Thinking…" bubble
    // by ChatMessage). The placeholder's jobId is filled in once the POST returns so the WS
    // handler can match it.
    var optimisticPendingMid = 'pending_local_' + Date.now() + '_' + Math.random().toString(36).slice(2,8);
    setMessages(prev => ([
      ...prev,
      { mid: 'u_' + Date.now() + '_' + Math.random().toString(36).slice(2,8), role: 'user', content: msg, timestamp: new Date() },
      { mid: optimisticPendingMid, role: 'assistant', content: '', pending: true, userMessage: msg, timestamp: new Date() }
    ]));
    setSending(true);

    // Resolve which sessionId this message belongs to. If we're in __new__ this lazily
    // mints a fresh client sessionId so new chats can't merge into the server's hour-bucket.
    var requestSessionId = null;
    if (selectedSession === '__new__') requestSessionId = ensurePendingNewSessionId();
    else if (selectedSession && selectedSession !== 'all') requestSessionId = selectedSession;

    try {
      const res = await authFetch('/api/aiassistant/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Async-Chat': '1' },
        body: JSON.stringify({ message: msg, callerTier: chatAs, sessionId: requestSessionId || undefined })
      });
      const data = await res.json();
      if (res.ok && data.mode === 'async' && data.jobId) {
        // Backfill the jobId onto the already-visible placeholder so the WS handler can target it.
        setMessages(prev => prev.map(function(m) {
          return m.mid === optimisticPendingMid ? { ...m, mid: 'pending_' + data.jobId, jobId: data.jobId } : m;
        }));
        // Recovery: WS delivery can drop (iOS Safari backgrounding, network flap, server
        // queueing on disconnect). Poll loadHistory at multiple intervals — saveChatTurn
        // commits during the LLM call so the assistant reply IS in Mongo, the client just
        // missed the WS event. Cancel the chain once matched.
        var jobId = data.jobId;
        var userMsg = msg;
        var recovered = false;
        var norm = function(s) { return String(s || '').trim().toLowerCase(); };
        var tryRecover = async function() {
          if (recovered) return;
          try {
            var hist = await loadHistory();
            if (!hist || recovered) return;
            var match = null;
            var needle = norm(userMsg);
            for (var s = 0; s < hist.length && !match; s++) {
              var msgs = hist[s].messages || [];
              for (var i = 0; i < msgs.length - 1; i++) {
                if (msgs[i].role !== 'user') continue;
                // Match if stored user msg equals (trimmed/lowercased) OR contains the typed msg
                // — catches small server-side sanitization tweaks that derail exact-match.
                var stored = norm(msgs[i].content);
                if ((stored === needle || stored.indexOf(needle) >= 0 || needle.indexOf(stored) >= 0) && msgs[i+1] && msgs[i+1].role === 'assistant') {
                  match = msgs[i+1];
                }
              }
            }
            if (!match || !(match.content || '').trim()) return;
            var applied = false;
            setMessages(function(prev) {
              var stillPending = prev.find(function(m) { return m.jobId === jobId && m.pending; });
              if (!stillPending) return prev;
              applied = true;
              return prev.map(function(m) {
                return m.jobId === jobId
                  ? { ...m, content: match.content, toolCalls: match.toolCalls || undefined, llmTrace: match.llmTrace || undefined, reasoning: match.reasoning || undefined, pending: false, timestamp: match.timestamp || m.timestamp, userMessage: undefined }
                  : m;
              });
            });
            if (applied) {
              recovered = true;
              setSending(false);
              sendingRef.current = false;
            }
          } catch (e) {}
        };
        // Schedule a polling ladder. Each attempt re-fetches history; the first match wins.
        [5000, 15000, 30000, 60000, 80000].forEach(function(delay) { setTimeout(tryRecover, delay); });
        // Hard timeout: if still nothing after 90s, show error
        setTimeout(function() {
          if (recovered) return;
          setMessages(function(prev) {
            var stillPending = prev.find(function(m) { return m.jobId === jobId && m.pending; });
            if (!stillPending) return prev;
            setSending(false);
            sendingRef.current = false;
            return prev.map(function(m) { return m.jobId === jobId ? { mid: 'err_' + jobId, role: 'assistant', content: 'Response timed out. Please try again.', error: true, timestamp: new Date() } : m; });
          });
        }, 90000);
        // Note: setSending stays true until WS event arrives; handled in WS handler.
        return;
      }
      if (res.ok) {
        const failedTools = (data.toolCalls || []).filter(tc => tc.error);
        const assistantText = data.response;
        // Replace the optimistic Thinking… placeholder with the real reply (sync path).
        setMessages(prev => prev.map(function(m) {
          return m.mid === optimisticPendingMid
            ? { mid: 'r_' + Date.now(), role: 'assistant', content: assistantText, timestamp: data.timestamp, toolCalls: failedTools.length > 0 ? failedTools : undefined, llmTrace: data.llmTrace || undefined, reasoning: data.reasoning || undefined }
            : m;
        }));
        syncHistoryAfterSend(msg, assistantText);
        if (voiceMode) speakText(data.response);
      } else {
        setMessages(prev => prev.map(function(m) {
          return m.mid === optimisticPendingMid
            ? { mid: 'err_' + Date.now(), role: 'assistant', content: typeof data.error === 'string' ? data.error : JSON.stringify(data.error) || 'Agent error', timestamp: new Date(), error: true }
            : m;
        }));
      }
    } catch (e) {
      setMessages(prev => prev.map(function(m) {
        return m.mid === optimisticPendingMid
          ? { mid: 'err_' + Date.now(), role: 'assistant', content: 'Failed to reach agent', timestamp: new Date(), error: true }
          : m;
      }));
    } finally {
      // For sync path; async path clears these in WS handler
      if (sendingRef.current) {
        // Will be cleared by WS handler in async mode
      }
      setSending(false);
      sendingRef.current = false;
    }
  };

  var handleInputKeyDown = function(e) {
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (!canTextCaller) return;
    e.preventDefault();
    if (e.metaKey || e.ctrlKey) {
      sendChat(null, 'ai');
    } else {
      sendChat(null, sendTarget);
    }
  };

  var lastSendTapRef = useRef(0);
  var sendTapTimerRef = useRef(null);
  var handleSendTap = function(e) {
    e.preventDefault();
    if (!chatInput.trim() || sending || status.status !== 'running') return;
    var now = Date.now();
    if (now - lastSendTapRef.current < 400) {
      clearTimeout(sendTapTimerRef.current);
      lastSendTapRef.current = 0;
      setSendTarget(function(prev) { return prev === 'sms' ? 'ai' : prev === 'ai' ? 'both' : 'sms'; });
    } else {
      lastSendTapRef.current = now;
      sendTapTimerRef.current = setTimeout(function() {
        sendChat(null, sendTarget);
        lastSendTapRef.current = 0;
      }, 400);
    }
  };

  const resendMessage = useCallback((msg) => {
    if (sending || sendingRef.current || !msg?.content) return;
    setChatInput(msg.content);
  }, [sending]);

  const saveConfig = async (updates) => {
    // Safe serialize — strip cycles and non-serializable values
    const seen = new WeakSet();
    const body = JSON.stringify(updates, (key, val) => {
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) return undefined;
        seen.add(val);
      }
      return val;
    });
    const res = await authFetch('/api/aiassistant/config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: body
    });
    if (res.ok) {
      setConfig(prev => ({ ...prev, config: { ...prev.config, ...JSON.parse(body) } }));
    }
  };

  const audioCtxRef = useRef(null);
  const getAudioCtx = () => {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
    return audioCtxRef.current;
  };

  const speakText = async (text, onDone) => {
    setSpeaking(true);
    try {
      const ctx = getAudioCtx();
      const res = await authFetch('/api/aiassistant/speak', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text })
      });
      if (!res.ok) throw new Error('TTS failed');
      const arrayBuf = await res.arrayBuffer();
      const audioBuf = await ctx.decodeAudioData(arrayBuf);
      const source = ctx.createBufferSource();
      source.buffer = audioBuf;
      source.connect(ctx.destination);
      ttsAudioRef.current = source;
      source.onended = () => { ttsAudioRef.current = null; setSpeaking(false); if (onDone) onDone(); };
      source.start(0);
    } catch(e) {
      console.error('TTS error:', e);
      setSpeaking(false);
      if (onDone) onDone();
    }
  };

  useEffect(() => { syncHistoryAfterSendRef.current = syncHistoryAfterSend; });
  useEffect(() => { speakTextRef.current = speakText; });

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = 0; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          final += event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }

      // Show live transcript as user speaks
      if (interim) {
        setLiveTranscript(interim);
      }

      if (final) {
        setLiveTranscript('');
        setListening(false);
        const transcript = final.trim();
        // In voice mode, auto-send
        if (voiceModeRef.current) {
          // Guard: skip if mic picked up TTS output (echo detection)
          if (lastReplyRef.current) {
            const last = lastReplyRef.current.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim();
            const heard = transcript.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim();
            // Check if any 4+ word sequence from heard appears in last reply
            const heardWords = heard.split(/\s+/);
            const chunk = heardWords.slice(0, Math.min(5, heardWords.length)).join(' ');
            if (chunk.length > 8 && last.includes(chunk)) {
              console.log('[Echo detected] Skipping:', transcript.substring(0, 50));
              if (voiceModeRef.current) setTimeout(startListening, 800);
              return;
            }
          }
          lastReplyRef.current = ''; // clear for new user input
          if (sendingRef.current) return; // prevent double send
          sendingRef.current = true;
          setChatInput('');
          // Send voice turns to the currently-selected session so they don't orphan into a new
          // session each time. Same lazy-mint logic as the typed sendChat path.
          var voiceRequestSessionId = null;
          if (selectedSession === '__new__') voiceRequestSessionId = ensurePendingNewSessionId();
          else if (selectedSession && selectedSession !== 'all') voiceRequestSessionId = selectedSession;
          // Insert user msg + visible Thinking… placeholder in one render so the UI doesn't go
          // blank during LLM + TTS roundtrip (5-10s+).
          var voicePendingMid = 'pending_voice_' + Date.now() + '_' + Math.random().toString(36).slice(2,8);
          setMessages(prev => ([
            ...prev,
            { role: 'user', content: transcript, timestamp: new Date() },
            { mid: voicePendingMid, role: 'assistant', content: '', pending: true, userMessage: transcript, timestamp: new Date() }
          ]));
          setSending(true);
          authFetch('/api/aiassistant/chat', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: transcript, callerTier: chatAs, sessionId: voiceRequestSessionId || undefined })
          }).then(r => r.json()).then(data => {
            const reply = data.response || data.error || 'No response';
            const failedTools = (data.toolCalls || []).filter(tc => tc.error);
            // Replace the Thinking… placeholder with the real reply so it doesn't linger.
            setMessages(prev => prev.map(function(m) {
              return m.mid === voicePendingMid
                ? { mid: 'r_' + Date.now(), role: 'assistant', content: reply, timestamp: new Date(), error: !!data.error, toolCalls: failedTools.length > 0 ? failedTools : undefined, llmTrace: data.llmTrace || undefined, reasoning: data.reasoning || undefined }
                : m;
            }));
            if (!data.error) syncHistoryAfterSend(transcript, reply);
            setSending(false);
            sendingRef.current = false;
            lastReplyRef.current = reply;
            speakText(reply);
          }).catch(() => {
            setMessages(prev => prev.map(function(m) {
              return m.mid === voicePendingMid
                ? { mid: 'err_' + Date.now(), role: 'assistant', content: 'Failed to reach agent', timestamp: new Date(), error: true }
                : m;
            }));
            setSending(false);
            sendingRef.current = false;
          });
        } else {
          setChatInput(prev => (prev ? prev + ' ' : '') + transcript);
        }
      }
    };
    recognition.onerror = () => { setListening(false); setLiveTranscript(''); };
    recognition.onend = () => { if (!voiceModeRef.current) { setListening(false); setLiveTranscript(''); } };
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const ttsAudioRef = useRef(null);
  const toggleVoice = () => {
    if (listening || speaking) {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (ttsAudioRef.current) { try { ttsAudioRef.current.stop(); } catch(e) {} ttsAudioRef.current = null; }
      setListening(false);
      setSpeaking(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Speech recognition not supported in this browser'); return; }
    startListening();
  };

  // Keep ref in sync
  useEffect(() => { voiceModeRef.current = voiceMode; }, [voiceMode]);

  const toggleVoiceMode = () => {
    getAudioCtx(); // unlock audio on user gesture
    // If speaking, stop TTS
    if (speaking) {
      if (ttsAudioRef.current) { try { ttsAudioRef.current.stop(); } catch(e) {} ttsAudioRef.current = null; }
      setSpeaking(false);
      return;
    }
    // If listening, stop listening
    if (listening) {
      if (recognitionRef.current) recognitionRef.current.stop();
      setListening(false);
      setLiveTranscript('');
      return;
    }
    // Start a new listen cycle (push-to-talk)
    setVoiceMode(true);
    voiceModeRef.current = true;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Speech recognition not supported'); setVoiceMode(false); return; }
    startListening();
  };

  const toggleSelectMsg = (ts) => {
    setSelectedMsgs(prev => {
      const next = new Set(prev);
      if (next.has(ts)) next.delete(ts); else next.add(ts);
      return next;
    });
  };

  const getMsgByTs = (ts) => {
    var found = messages.find(m => m.timestamp === ts);
    if (!found) {
      sessions.some(s => {
        found = (s.messages || []).find(m => m.timestamp === ts);
        return !!found;
      });
    }
    return found;
  };

  const getSessionByMsgTs = (ts) => sessions.find(s => (s.messages || []).some(m => m.timestamp === ts)) || null;

  var relativeTime = function(ts) {
    var diff = Date.now() - new Date(ts).getTime();
    var mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return mins + 'm ago';
    var hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h ago';
    var days = Math.floor(hrs / 24);
    if (days < 7) return days + 'd ago';
    return new Date(ts).toLocaleDateString();
  };

  var sessionMeta = function(s) {
    var msgs = s.messages || [];
    var hasAI = msgs.some(function(m) { return m.role === 'assistant'; });
    var tags = [];
    if (s.channel === 'sms') tags.push({icon:'💬',label:'SMS'});
    else if (s.channel === 'voice') tags.push({icon:'📞',label:'Call'});
    else if (s.channel === 'whatsapp') tags.push({icon:'💚',label:'WA'});
    else tags.push({icon:'🌐',label:'Web'});
    if (hasAI) tags.push({icon:'🤖',label:'AI'});
    try {
      var lastSeen = localStorage.getItem('nf_last_seen_' + s.sessionId);
      var latestMsg = msgs[msgs.length - 1];
      var hasNew = !lastSeen || (latestMsg && new Date(latestMsg.timestamp).getTime() > parseInt(lastSeen, 10));
    } catch(e) { var hasNew = false; }
    return { msgs: msgs.length, tags: tags, hasNew: hasNew, caller: s.caller || 'Unknown', channel: s.channel || 'web', time: relativeTime(s.startTime) };
  };

  const formatShareHeader = (session, msg) => {
    var caller = session ? session.caller : (msg && (msg.caller || (msg.role === 'user' ? 'You' : 'AI')));
    var tier = session ? session.callerTier : (msg && msg.callerTier);
    var channel = session ? session.channel : (msg && msg.channel);
    var time = session ? session.startTime : (msg && msg.timestamp ? new Date(msg.timestamp) : null);
    var parts = [];
    if (caller) parts.push(caller);
    if (tier) parts.push(tier.toUpperCase());
    if (channel) parts.push(channel === 'voice' ? '📞 VOICE' : channel === 'sms' ? '💬 SMS' : '🌐 ' + channel.toUpperCase());
    if (time) parts.push(new Date(time).toLocaleString());
    return parts.length ? parts.join(' ') : '';
  };

  const deleteSelectedMsgs = async () => {
    if (selectedMsgs.size === 0) return;
    var tsToDelete = [...selectedMsgs];
    setMessages(prev => prev.filter(m => !selectedMsgs.has(m.timestamp)));
    setSessions(prev => prev.map(s => ({...s, messages: (s.messages || []).filter(m => !selectedMsgs.has(m.timestamp))})));
    setSelectedMsgs(new Set());
    setSelectMode(false);
    try {
      await authFetch('/api/aiassistant/history/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timestamps: tsToDelete })
      });
    } catch (e) {}
  };

  var handleShareSelected = function() {
    var groups = {};
    [...selectedMsgs].forEach(function(ts) {
      var m = getMsgByTs(ts);
      if (!m || !m.content) return;
      var s = getSessionByMsgTs(ts);
      var key = s ? s.sessionId : 'live';
      if (!groups[key]) groups[key] = { session: s, messages: [] };
      groups[key].messages.push(m);
    });
    var texts = [];
    var selections = [];
    var groupList = Object.values(groups);
    groupList.sort(function(a, b) {
      var at = a.session ? new Date(a.session.startTime || 0) : new Date(8640000000000000);
      var bt = b.session ? new Date(b.session.startTime || 0) : new Date(8640000000000000);
      return at - bt;
    });
    groupList.forEach(function(g) {
      g.messages.sort(function(a, b) { return new Date(a.timestamp || 0) - new Date(b.timestamp || 0); });
      var hdr = formatShareHeader(g.session, g.messages[0]);
      if (hdr) texts.push(hdr);
      var senderName = g.session ? g.session.caller : '';
      g.messages.forEach(function(m) {
        var roleLabel = m.role === 'assistant' ? 'AI Assistant' : (senderName || 'You');
        var timeStr = m.timestamp ? new Date(m.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
        texts.push(roleLabel + (timeStr ? ' (' + timeStr + ')' : '') + ': ' + m.content);
        texts.push('');
      });
      if (g.session && g.session.sessionId) {
        selections.push({
          sessionId: g.session.sessionId,
          sessionLabel: hdr || '',
          timestamps: g.messages.map(function(m) { return m.timestamp; })
        });
      }
    });
    if (texts.length) setShareMsg({ text: texts.join('\n'), selections: selections });
  };

  var handleShareCurrentSession = function() {
    var sessionsToShare = [];
    if (selectedSession === 'all') {
      var nSessions = (tierFilteredSessions || sessions || []).length;
      var ok = window.confirm('⚠️ Share ALL ' + nSessions + ' sessions?\n\nThis will create a single shared page containing every message across every session.\n\nUsually you want to pick one session first.');
      if (!ok) return;
      sessionsToShare = (tierFilteredSessions && tierFilteredSessions.length ? tierFilteredSessions : sessions) || [];
    } else if (selectedSession === '__new__') {
      window.alert('Nothing to share yet — send a message first.');
      return;
    } else {
      var s = sessions.find(function(x) { return x.sessionId === selectedSession; });
      if (!s) return;
      sessionsToShare = [s];
    }

    var texts = [];
    var selections = [];
    sessionsToShare.forEach(function(s) {
      var msgs = (s.messages || []).filter(function(m) { return (m.role === 'user' || m.role === 'assistant') && m.content && String(m.content).trim(); });
      if (msgs.length === 0) return;
      var hdr = formatShareHeader(s, msgs[0]);
      if (hdr) texts.push(hdr);
      var senderName = s.caller || '';
      msgs.forEach(function(m) {
        var roleLabel = m.role === 'assistant' ? 'AI Assistant' : (senderName || 'You');
        var timeStr = m.timestamp ? new Date(m.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
        texts.push(roleLabel + (timeStr ? ' (' + timeStr + ')' : '') + ': ' + m.content);
        texts.push('');
      });
      selections.push({
        sessionId: s.sessionId,
        sessionLabel: hdr || '',
        timestamps: msgs.map(function(m) { return m.timestamp; })
      });
    });

    if (selections.length === 0) {
      window.alert('Nothing to share in this session.');
      return;
    }
    setShareMsg({ text: texts.join('\n'), selections: selections });
  };

  var handleShare = function(arg) {
    if (arg && typeof arg === 'object' && 'msg' in arg) {
      var m = arg.msg || {};
      var s = m.timestamp ? getSessionByMsgTs(m.timestamp) : null;
      var hdr = s ? formatShareHeader(s, m) : '';
      var sel = (s && s.sessionId && m.timestamp) ? [{
        sessionId: s.sessionId,
        sessionLabel: hdr || '',
        timestamps: [m.timestamp]
      }] : [];
      setShareMsg({ text: arg.text || '', selections: sel });
      return;
    }
    setShareMsg({ text: typeof arg === 'string' ? arg : '', selections: [] });
  };
  var handleCreateNote = async function(text) {
    try { await authFetch('/api/notes', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({title:'From Chat',body:text,color:'#fef3c7'})}); } catch(e) {}
  };
  var handleCreateTask = async function(text) {
    try { await authFetch('/api/tasks', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({title:'From Chat',description:text,priority:'medium'})}); } catch(e) {}
  };

  if (loading) return <div className="ai-agent-loading">Loading...</div>;

  const tierFilteredSessions = sessions.filter((session) => {
    if (selectedTier !== 'all' && session.callerTier !== selectedTier) return false;
    var hasContent = (session.messages || []).some(function(m) {
      return (m.role === 'user' || m.role === 'assistant') && m.content && String(m.content).trim();
    });
    if (!hasContent) return false;
    return true;
  });
  const activeSession = selectedSession === 'all' || selectedSession === '__new__' ? null : (sessions.find(s => s.sessionId === selectedSession) || null);
  const isSmsSession = activeSession && activeSession.channel === 'sms';
  const isVoiceSession = activeSession && activeSession.channel === 'voice';
  const smsCaller = activeSession ? activeSession.caller : null;
  // Both SMS and voice sessions have a textable caller number — the AI/SMS/Both
  // send-target picker should show in both, not only SMS sessions.
  const canTextCaller = !!(smsCaller && (isSmsSession || isVoiceSession));

  var forwardSmsHandler = isSmsSession && smsCaller ? async function(text) {
    try {
      await authFetch('/api/conversations/send', {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ to: smsCaller, body: (text||'').substring(0, 1600), from: (config?.phoneNumber || '') })
      });
      setMessages(prev => [...prev, { mid: 'fwd_' + Date.now(), role: 'system', content: 'Forwarded to ' + smsCaller, timestamp: new Date() }]);
    } catch (e) {}
  } : null;

  const filteredSessions = tierFilteredSessions.filter((session) => {
    if (selectedSession !== 'all' && session.sessionId !== selectedSession) return false;
    return true;
  });

  const historicalSessions = sortByTimestamp(
    filteredSessions,
    'startTime'
  ).map((session, index) => ({
    ...session,
    color: SESSION_COLORS[index % SESSION_COLORS.length],
    messages: sortByTimestamp(session.messages || [])
  }));

  const hasLiveActivity = messages.length > 0 || liveTranscript || sending;

  // Not enabled — show activation screen
  if (!status?.enabled) {
    return (
      <div className="ai-agent-activate">
        <div className="activate-card">
          <div className="activate-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#1E203F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><circle cx="9" cy="9" r="1" fill="#1E203F"/><circle cx="15" cy="9" r="1" fill="#1E203F"/></svg>
          </div>
          <h2>Activate your AI Assistant</h2>
          <p>Get your own AI assistant for calls, texts, and chat, all from your NumFree number or directly from this dashboard.</p>
          <ul className="activate-features">
            <li>Chat directly from the web</li>
            <li>Answer phone calls with AI voice</li>
            <li>Reply to SMS and WhatsApp automatically</li>
            <li>Export and run on your own hardware anytime</li>
          </ul>
          <button data-testid="ai-enable-btn" className="btn-activate" onClick={enableAgent} disabled={enabling}>
            {enabling ? 'Setting up your agent...' : 'Enable AI Assistant'}
          </button>
        </div>
      </div>
    );
  }

  return (
     <div className="ai-agent-dashboard">
       <div className="ai-agent-header">
         <h2>{config?.config?.agentName || 'AI Assistant'}</h2>
      </div>

      <div className="ai-agent-tabs">
        {TABS.map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >{tab}</button>
        ))}
      </div>

      <div className="ai-agent-content">
        {activeTab === 'Chat' && (
          <div className="tab-dashboard">
            <div className="agent-chat">
              <div className="chat-toolbar">
                <div className="chat-as-toggle">
                  <button className={`chat-as-icon ${chatAs === 'owner' ? 'active' : ''}`} onClick={() => setChatAs('owner')} title="Owner">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </button>
                  <button className={`chat-as-icon guest ${chatAs === 'guest' ? 'active' : ''}`} onClick={() => setChatAs('guest')} title="Guest">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6M23 11h-6"/></svg>
                  </button>
                </div>
                <div className="chat-session-picker" ref={sessionPickerTriggerRef}>
                  <button type="button" className="chat-session-picker-btn" onClick={() => setSessionPickerOpen(o => !o)}>
                    {selectedSession === 'all' ? 'All sessions' : selectedSession === '__new__' ? 'New session' : (
                      (() => { var s = sessions.find(x => x.sessionId === selectedSession); return s ? ((s.channel === 'voice' ? '📞 ' : s.channel === 'sms' ? '💬 ' : s.channel === 'whatsapp' ? '💚 ' : '🌐 ') + (s.caller || 'Unknown')) : 'Session'; })()
                    )}
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                </div>
                {sessionPickerOpen && createPortal(
                  <div className="chat-session-popover" role="menu" style={{position:'fixed',top:sessionPickerTriggerRef.current ? sessionPickerTriggerRef.current.getBoundingClientRect().bottom + 4 : 120,left:sessionPickerTriggerRef.current ? Math.min(sessionPickerTriggerRef.current.getBoundingClientRect().left, window.innerWidth - 320) : 16,width:300,maxHeight:Math.min(window.innerHeight-160,480),zIndex:1001,overflowY:'auto',background:'#fff',border:'1px solid #e5e5e5',borderRadius:12,boxShadow:'0 8px 24px rgba(0,0,0,0.15)',padding:6}}>
                    <button type="button" className={`chat-session-pick-row ${selectedSession === 'all' ? 'active' : ''}`} onClick={() => { setSelectedSession('all'); setSessionPickerOpen(false); }}>
                      <span className="chat-session-pick-name">All sessions</span>
                    </button>
                    {!(selectedSession === '__new__' && messages.length === 0) && (
                      <button type="button" className="chat-session-pick-row" style={{borderBottom:'1px solid #eee',marginBottom:4,paddingBottom:10}} onClick={() => { newChat(); setSessionPickerOpen(false); }}>
                        <span className="chat-session-pick-name">+ New Session</span>
                      </button>
                    )}
                    {tierFilteredSessions.map(s => {
                      var meta = sessionMeta(s);
                      var select = function() {
                        try { localStorage.setItem('nf_last_seen_' + s.sessionId, Date.now()); } catch(e) {}
                        setSelectedSession(s.sessionId);
                        setSessionPickerOpen(false);
                      };
                      return (
                        <button key={s.sessionId} type="button" className={`chat-session-pick-row ${selectedSession === s.sessionId ? 'active' : ''}`} onClick={select}>
                          <div className="chat-session-pick-line1">
                            <span className="chat-session-pick-name">{(s.channel === 'voice' ? '📞 ' : s.channel === 'sms' ? '💬 ' : s.channel === 'whatsapp' ? '💚 ' : '🌐 ') + meta.caller}</span>
                            <span className="chat-session-pick-time">{meta.time}</span>
                          </div>
                          <div className="chat-session-pick-line2">
                            <span className="chat-session-pick-tags">
                              {meta.tags.map(function(t,i) { return <span key={i} className="chat-session-pick-tag">{t.icon} {t.label}</span>; })}
                            </span>
                            <span className="chat-session-pick-count">{meta.msgs} msgs</span>
                            {meta.hasNew && <span className="chat-session-pick-new">NEW</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>,
                  document.body
                )}
                <button
                  type="button"
                  className={`chat-toolbar-icon ${showAllThinking ? 'active' : ''}`}
                  onClick={() => setShowAllThinking(v => !v)}
                  title={showAllThinking ? 'Hide all thinking' : 'Show all thinking'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className="chat-jump-bottom"
                  onClick={jumpToLatest}
                  title="Jump to latest messages"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 5v12"/>
                    <path d="m19 12-7 7-7-7"/>
                  </svg>
                  <span>Latest</span>
                </button>
                {!selectMode && (
                  <button
                    type="button"
                    className="chat-toolbar-icon"
                    onClick={handleShareCurrentSession}
                    title={selectedSession === 'all' ? 'Share — picks the whole transcript (you will be warned)' : 'Share this session'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                  </button>
                )}
                {selectMode ? (
                  <div className="select-controls-compact">
                    <span>{selectedMsgs.size}</span>
                    <button onClick={handleShareSelected} disabled={selectedMsgs.size === 0} title="Share selected">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                    </button>
                    <button onClick={() => { setSelectMode(false); setSelectedMsgs(new Set()); }} title="Cancel">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                    <button className="btn-delete" onClick={deleteSelectedMsgs} disabled={selectedMsgs.size === 0} title="Delete selected">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                    </button>
                  </div>
                ) : (
                  <button className="chat-toolbar-icon" onClick={() => setSelectMode(true)} title="Select messages">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
                  </button>
                )}
              </div>
              <div className="chat-messages" ref={chatMessagesRef}>
                {historicalSessions.length === 0 && !hasLiveActivity && (
                  <div className="chat-empty">Send a message to start chatting with your agent</div>
                )}
                {historicalSessions.map((session) => (
                  <div key={session.sessionId} className={`session-block ${session.channel ? 'channel-' + session.channel : ''}`} style={{borderLeftColor: session.channel === 'voice' ? '#00897b' : session.channel === 'sms' ? '#e53935' : session.channel === 'whatsapp' ? '#43a047' : session.color}}>
                    <div className="session-header">
                      <span className="session-caller">
                        {session.channel === 'voice' ? (session.caller === 'owner' ? 'You (phone)' : session.caller) : session.caller === 'owner' ? 'You (web)' : session.caller}
                        <span className={`session-tier-badge ${session.callerTier === 'guest' ? 'guest' : 'owner'}`}>
                          {session.callerTier || 'owner'}
                        </span>
                        <span className={`session-channel-badge ${session.channel || 'web'}`}>
                          {session.channel === 'voice' ? '📞 Voice' : session.channel === 'sms' ? '💬 SMS' : session.channel === 'whatsapp' ? '💚 WhatsApp' : '🌐 Web'}
                        </span>
                      </span>
                      <span className="session-meta">
                        {new Date(session.startTime).toLocaleString()}
                        {session.endTime && (() => {
                          const dur = Math.round((new Date(session.endTime) - new Date(session.startTime)) / 1000);
                          return dur > 0 ? ` — ${Math.floor(dur/60)}m ${dur%60}s` : '';
                        })()}
                      </span>
                    </div>
                    {session.messages.map((msg, i) => (
                      <ChatMessage key={`${session.sessionId}-${msg.timestamp || i}`} msg={msg} selectMode={selectMode} selectedMsgs={selectedMsgs} toggleSelectMsg={toggleSelectMsg} setActiveTab={setActiveTab} onResend={resendMessage} onShare={handleShare} onForwardSms={forwardSmsHandler} showAllThinking={showAllThinking} />
                    ))}
                  </div>
                ))}
                {hasLiveActivity && (
                  <div className="session-block current-session" style={{borderLeftColor: '#1E203F'}}>
                    {messages.map((msg, i) => {
                      var prevSession = i > 0 && messages[i-1]._sessionId;
                      var showDivider = msg._sessionId && msg._sessionId !== prevSession;
                      return (
                        <React.Fragment key={`live-${msg.timestamp || i}`}>
                           {showDivider && (
                             <div className={`session-block ${msg._channel ? 'channel-' + msg._channel : ''}`} style={{borderLeftColor: msg._channel === 'voice' ? '#00897b' : msg._channel === 'sms' ? '#e53935' : msg._channel === 'whatsapp' ? '#43a047' : '#1E203F'}}>
                               <div className="session-header">
                                 <span className="session-caller">
                                   {msg._channel === 'voice' ? (msg._caller === 'owner' ? 'You (phone)' : msg._caller) : msg._caller === 'owner' ? 'You (web)' : msg._caller}
                                   <span className={`session-tier-badge ${msg._callerTier === 'guest' ? 'guest' : 'owner'}`}>{msg._callerTier || 'owner'}</span>
                                   <span className={`session-channel-badge ${msg._channel || 'web'}`}>{msg._channel === 'voice' ? '📞 Voice' : msg._channel === 'sms' ? '💬 SMS' : msg._channel === 'whatsapp' ? '💚 WhatsApp' : '🌐 Web'}</span>
                                 </span>
                                 <span className="session-meta">
                                   {msg._startTime ? new Date(msg._startTime).toLocaleString() : ''}
                                   {msg._endTime && (() => { var dur = Math.round((new Date(msg._endTime) - new Date(msg._startTime)) / 1000); return dur > 0 ? ' — ' + Math.floor(dur/60) + 'm ' + (dur%60) + 's' : ''; })()}
                                 </span>
                               </div>
                             </div>
                           )}
                          <ChatMessage msg={msg} selectMode={selectMode} selectedMsgs={selectedMsgs} toggleSelectMsg={toggleSelectMsg} setActiveTab={setActiveTab} onResend={resendMessage} onShare={handleShare} onForwardSms={forwardSmsHandler} showAllThinking={showAllThinking} />
                        </React.Fragment>
                      );
                    })}
                    {liveTranscript && (
                      <div className="chat-msg user live-transcript">
                        <div className="chat-msg-content">
                          <span className="live-text">{liveTranscript}</span>
                          <span className="live-dot-pulse">...</span>
                        </div>
                      </div>
                     )}
                      {sending && (
                        <div className="chat-msg assistant thinking thinking-activity">
                          <div className="chat-msg-content thinking-activity-content">
                            {status.status !== 'running' ? (
                              <div className="act-line">Agent starting...</div>
                            ) : activeTools.length === 0 ? (
                              <div className="act-line"><span className="act-pulse"/>Thinking...</div>
                            ) : (
                              activeTools.map((t, i) => {
                                var elapsed = Math.round((Date.now() - new Date(t.ts).getTime()) / 1000);
                                var icon = t.step === 'tool_result' ? '✓' : '🔍';
                                var label = t.step === 'tool_result' ? t.name + ' done' : t.name;
                                return <div key={i} className="act-line">{icon} {label} · {elapsed}s</div>;
                              })
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                )}
                <div ref={chatBottomRef} style={{ height: 1 }} />
              </div>
              <form className="chat-input-form" onSubmit={sendChat} data-testid="ai-chat-form">
                <div className="chat-input-wrap">
                  {availableModels.length > 0 && (
                    <button
                      ref={modelTriggerRef}
                      type="button"
                      className={`chat-model-icon-btn ${modelPickerOpen ? 'open' : ''}`}
                      onClick={openModelPicker}
                      disabled={savingModel}
                      title={`Model: ${shortModelName(currentModel)}`}
                      aria-label="Choose model"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="4" y="4" width="16" height="16" rx="3"/>
                        <path d="M9 9h6v6H9z"/>
                        <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3"/>
                      </svg>
                    </button>
                  )}
                  <input
                    data-testid="ai-chat-input"
                    className="chat-input-bare"
                    type="text"
                    inputMode="text"
                    autoComplete="off"
                    enterKeyHint="send"
                     placeholder={chatAs === 'guest' ? 'Type as guest caller...' : isSmsSession ? 'Reply or @ai to ask AI...' : 'Type a message...'}
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    disabled={sending || status.status !== 'running'}
                    onKeyDown={handleInputKeyDown}
                  />
                </div>
                <button data-testid="ai-voice-btn" type="button" className={`voice-btn ${(listening || speaking) ? 'active' : ''} ${speaking ? 'speaking' : ''}`} onClick={toggleVoiceMode} disabled={sending || status.status !== 'running'} title={speaking ? 'Speaking...' : listening ? 'Listening...' : `Speak as ${chatAs === 'guest' ? 'Guest' : 'Owner'}`}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                </button>
                {canTextCaller ? (
                  <div className="chat-send-split" ref={sendTriggerRef}>
                    <button
                      data-testid="ai-chat-send"
                      type="button"
                      className={`chat-send-btn ${sendTarget}`}
                      disabled={sending || !chatInput.trim() || status.status !== 'running'}
                      onClick={handleSendTap}
                      title={sendTarget === 'ai' ? 'Send to AI' + ' · Enter' + ' · double-tap for SMS' : sendTarget === 'sms' ? 'Reply to ' + smsCaller + ' · Enter' + ' · double-tap for AI' : 'Send to both' + ' · Enter' + ' · double-tap cycles'}
                    >
                      {sendTarget === 'ai' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                      ) : sendTarget === 'sms' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/><line x1="12" y1="2" x2="12" y2="8" strokeWidth="2.5"/><line x1="10" y1="5" x2="14" y2="5" strokeWidth="2.5"/></svg>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`chat-send-caret ${sendPickerOpen ? 'open' : ''}`}
                      onClick={() => {
                        if (sendTriggerRef.current) {
                          var r = sendTriggerRef.current.getBoundingClientRect();
                          var style = sendTriggerRef.current.parentElement;
                        }
                        setSendPickerOpen(o => !o);
                      }}
                      title="Send target"
                    >
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                  </div>
                ) : (
                  <button data-testid="ai-chat-send" type="submit" disabled={sending || !chatInput.trim() || status.status !== 'running'}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                  </button>
                )}
                <span data-testid="ai-status-dot" className={`status-dot ${status.status === 'running' ? 'online' : 'offline'}`} title={status.status === 'running' ? 'Online' : 'Offline'} />
              </form>
              {sendPickerOpen && createPortal(
                <div
                  ref={sendPopoverRef}
                  className="chat-send-popover"
                  role="menu"
                  style={{position:'fixed',bottom:'72px',right:'56px',zIndex:1001}}
                  onClick={() => setSendPickerOpen(false)}
                >
                  {[
                    { target: 'ai', icon: 'M2.01 21L23 12 2.01 3 2 10l15 2-15 2z', label: 'AI', key: 'Enter' },
                    { target: 'sms', icon: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z', label: 'SMS', key: smsCaller ? smsCaller.substring(0,14) : 'SMS' },
                    { target: 'both', icon: 'M2.01 21L23 12 2.01 3 2 10l15 2-15 2zM12 3v6M10 6h4', label: 'Both', key: 'Shift+Enter' },
                  ].map(item => (
                    <button
                      key={item.target}
                      type="button"
                      className={'chat-send-option' + (sendTarget === item.target ? ' active' : '')}
                      onClick={() => { setSendTarget(item.target); setSendPickerOpen(false); }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={item.icon}/></svg>
                      <span className="chat-send-option-label">{item.label}</span>
                      <kbd>{item.key}</kbd>
                    </button>
                  ))}
                </div>,
                document.body
              )}
              {modelPickerOpen && modelPopoverCoords && createPortal(
                <div
                  ref={modelPopoverRef}
                  className="chat-model-popover-floating"
                  role="menu"
                  style={{
                    position: 'fixed',
                    bottom: modelPopoverCoords.bottom + 'px',
                    left: modelPopoverCoords.left + 'px',
                    width: modelPopoverCoords.width + 'px',
                    maxHeight: modelPopoverCoords.maxHeight + 'px',
                  }}
                >
                  {availableModels.map(m => {
                    const price = tierPriceLevel(m.tier);
                    const stars = tierStars(m.tier);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        className={`chat-model-row ${m.id === currentModel ? 'selected' : ''}`}
                        onClick={() => { changeModel(m.id); setModelPickerOpen(false); }}
                      >
                        <div className="chat-model-row-text">
                          <div className="chat-model-row-name">{shortModelName(m.id, m)}</div>
                          <div className="chat-model-row-meta">
                            <span className="model-price" title={price + ' / 4 cost'}>
                              {[1,2,3,4].map(n => (
                                <span key={n} className={n <= price ? 'on' : 'off'}>$</span>
                              ))}
                            </span>
                            <span className="model-stars" title={stars + ' / 4 quality'}>
                              {[1,2,3,4].map(n => (
                                <svg key={n} width="11" height="11" viewBox="0 0 24 24" className={n <= stars ? 'star on' : 'star off'} aria-hidden="true">
                                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                </svg>
                              ))}
                            </span>
                          </div>
                          {tierDescription(m.tier) && <div className="chat-model-row-desc">{tierDescription(m.tier)}</div>}
                        </div>
                        {m.id === currentModel && (
                          <svg className="chat-model-row-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        )}
                      </button>
                    );
                  })}
                </div>,
                document.body
              )}
            </div>
          </div>
        )}

        {activeTab === 'Settings' && (
          <SettingsTab config={config} onSave={saveConfig} status={status} applyTemplate={location.state?.applyTemplate} user={user} />
        )}
      </div>
      {shareMsg && <ShareSheet text={(shareMsg && shareMsg.text) || (typeof shareMsg === 'string' ? shareMsg : '')} selections={(shareMsg && shareMsg.selections) || []} onClose={() => setShareMsg(null)} onCreateNote={handleCreateNote} onCreateTask={handleCreateTask} />}
    </div>
  );
};


export default AIAgentDashboard;
